import axios from "axios";
import dayjs from "dayjs";
import { tokenManager } from "./services/tokenManager";

async function fetch_api_json(method, suffix_url) {
    try {
        const response = await fetch(process.env.REACT_APP_BACKEND_API_URL + suffix_url, {
            method: method,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Cache-Control": "no-cache, no-store, must-revalidate",
                Pragma: "no-cache",
                Expires: "0",
                ...tokenManager.getAuthHeaders(),
            },
        });
        const data = await response.json();
        return data;
    } catch (error) {
    }
}

export function logout() {
    // Revoke the session server-side: the backend deletes the session token and
    // clears the auth cookies, then answers with a redirect. That redirect (or a
    // CORS error while following it) happens after logout already succeeded, so
    // swallow any failure. The timeout guarantees the promise always settles so
    // the caller can navigate to the login page even if the backend hangs.
    return axios
        .post(process.env.REACT_APP_BACKEND_API_URL + "accounts/log_out", {}, { withCredentials: true, timeout: 8000 })
        .catch(() => {});
}

// Users
export function current_user() {
    return axios
        .get(process.env.REACT_APP_BACKEND_API_URL + "user/me", {
            headers: tokenManager.getAuthHeaders(),
        })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            return err;
        });
}

// PROPERTIES //

export function accessible_properties() {
    return fetch_api_json("GET", "/accessible_properties");
}

// Admin
export function property_list() {
    return axios
        .get(process.env.REACT_APP_BACKEND_API_URL + "/property", {
            headers: tokenManager.getAuthHeaders(),
        })
        .then((response) => {
            return response;
        })
        .catch((err) => {
            return err;
        });
}

export function property_by_id(id) {
    return axios
        .get(process.env.REACT_APP_BACKEND_API_URL + `property/${id}/`, {
            headers: tokenManager.getAuthHeaders(),
        })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            return err;
        });
}

// REPORTS //

// Accounts
export function ga_ga4_accounts() {
    return fetch_api_json("GET", "/ga/ga4/accounts");
}

export function ga_universal_accounts() {
    return fetch_api_json("GET", "/ga/universal/accounts");
}
export function ga_full_accounts() {
    return fetch_api_json("GET", "/ga/full/accounts");
}

export function ga_full_account(account_id, ga_type, property_id) {
    const encoded = encodeURIComponent(property_id);
    return fetch_api_json("GET", `/ga/full/account/${account_id}/${ga_type}/${encoded}`);
}

// Google Analytics (GA4 and Universal)
export function analytics_report(property_id, start_date, end_date) {
    const qs = `start_date=${start_date}&end_date=${end_date}`;
    return fetch_api_json("GET", `/property/${property_id}/ga/report?${qs}`);
}

// Google Analytics GA4
export function ga_ga4_report(property_id, start_date, end_date) {
    return analytics_report(property_id, start_date, end_date);
}

// Admin Google Analytics Function
export function ga_ga4_reports() {
    return fetch_api_json("GET", "/ga/ga4/reports");
}
export function ga_ga4_report_direct(account_id, property_id, start_date, end_date) {
    //  const query = {"contacts" : contact, "entities": entite, "themes" : theme, types: []};
    //  let encode = encodeURIComponent(JSON.stringify(query));
    const encoded = encodeURIComponent(property_id);
    const qs = `start_date=${start_date}&end_date=${end_date}`;
    return fetch_api_json("GET", `/ga/ga4/report/${account_id}/${encoded}?${qs}`);
}

// Google Analytics Universal
export function ga_universal_report(property_id, start_date, end_date) {
    return analytics_report(property_id, start_date, end_date);
}

// Google Ads
export function get_ads_report(property_id, start_date, end_date) {
    const qs = `start_date=${start_date}&end_date=${end_date}`;

    return fetch_api_json("GET", `/property/${property_id}/ads/report?${qs}`);
}

// All reports
export function all_reports(property_id, start_date, end_date) {
    const url = process.env.REACT_APP_BACKEND_API_URL + "property/" + property_id + "/all/report/?start_date=" + start_date + "&end_date=" + end_date;
    return axios
        .get(url, {
            headers: tokenManager.getAuthHeaders(),
        })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            return err;
        });
}

// Macro reports
export function macro_reports(start_date, end_date) {
    const url = process.env.REACT_APP_BACKEND_API_URL + "/properties/all/report/?start_date=" + start_date + "&end_date=" + end_date;
    return axios
        .get(url, {
            headers: tokenManager.getAuthHeaders(),
        })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            return err;
        });
}

// Admin function
export function ga_universal_reports() {
    return fetch_api_json("GET", "/ga/universal/reports");
}
export function ga_universal_report_direct(account_id, property_id, start_date, end_date) {
    const qs = `start_date=${start_date}&end_date=${end_date}`;

    const encoded = encodeURIComponent(property_id);
    return fetch_api_json("GET", `/ga/universal/report/${account_id}/${encoded}?${qs}`);
}

export function get_meta_analytics(meta_hotel_code, start_date, end_date) {
    const url = `${process.env.REACT_APP_TOOLS_API_URL}metas/analytics?engineCode=${meta_hotel_code}&start_date=${start_date}&end_date=${end_date}`;

    return axios
        .get(url, { headers: tokenManager.getAuthHeaders() })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            return err;
        });
}

export function get_meta_bookings(meta_hotel_code, start_date, end_date) {
    const url = `${process.env.REACT_APP_TOOLS_API_URL}metas/bookings?engineCode=${meta_hotel_code}&start_date=${start_date}&end_date=${end_date}`;

    return axios
        .get(url, { headers: tokenManager.getAuthHeaders() })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            return err;
        });
}

export async function get_all_reports_optimized(property_id, meta_hotel_code, start_date, end_date) {
    try {
        const allReportsPromise = all_reports(property_id, start_date, end_date);
        const metaAnalyticsPromise = meta_hotel_code 
            ? get_meta_analytics(meta_hotel_code, start_date, end_date)
            : Promise.resolve(null);

        const [allReportsResponse, metaAnalyticsData] = await Promise.all([
            allReportsPromise, 
            metaAnalyticsPromise
        ]);

        return {
            allReports: allReportsResponse,
            metaAnalytics: metaAnalyticsData
        };
    } catch (error) {
        throw error;
    }
}

// Réservation pour My Maison In Paris (Propriété 92)
export function get_my_maison_reservations(start_date, end_date) {
    const url = `${process.env.REACT_APP_TOOLS_API_URL}metas/mmip/bookings?start_date=${start_date}&end_date=${end_date}`;
    return axios
        .get(url, {
            headers: {
                'Authorization': `Bearer ${process.env.REACT_APP_MONITOR_TOKEN}`
            }
        })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            console.error("Error fetching MMIP reservations:", err);
            return [];
        });
}

export function get_my_maison_analytics(start_date, end_date) {
    const url = `${process.env.REACT_APP_TOOLS_API_URL}metas/mmip/analytics?start_date=${start_date}&end_date=${end_date}`;
    return axios
        .get(url, {
            headers: {
                'Authorization': `Bearer ${process.env.REACT_APP_MONITOR_TOKEN}`
            }
        })
        .then((response) => {
            return response.data;
        })
        .catch((err) => {
            console.error("Error fetching MMIP analytics:", err);
            return null;
        });
}
